import React, { useEffect, useState } from 'react';
import usePayment from 'state/payment/hooks/usePayment';
import useQuery from 'utils/hooks/useQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import DispensaryTransactionsList from './summary';
import PageHeading from 'components/heading';
import { TRANSACTION_TABS, DISPENSARY_LIST_TABS } from 'utils/constants';
import TransactionDetailsTab from './details';
import InvoicesTab from './invoices';
import DateRangeBar from 'components/date-range-bar';
import RefreshButton from 'components/refresh-button';
import useDateRange from 'utils/hooks/useDateRange';
import QUERY from 'utils/query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';

const DispensaryTransactions = (): JSX.Element => {
  const { payment } = usePayment();
  const location = useLocation();
  const tabNames = TRANSACTION_TABS;
  const urlParams = useQuery(location.search);

  const [triggerRefresh, setTriggerRefresh] = useState(false);

  // Primary tabs (top level)
  const primaryTabs = DISPENSARY_LIST_TABS.PRIMARY;
  const secondaryTabs = DISPENSARY_LIST_TABS.SECONDARY; // Only for payments

  // Use date range hook with URL sync
  const {
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    comparisonFrom,
    comparisonTo,
    dateFromISO,
    dateToISO,
  } = useDateRange({ syncToUrl: true, defaultRange: 'today' });

  const navigate = useNavigate();

  // Primary tab state
  const [primaryTab, setPrimaryTab] = useState('payments');

  // Secondary tab state (only for payments)
  const [secondaryTab, setSecondaryTab] = useState('summary');

  // For backwards compatibility with refresh component
  const activeTab = primaryTab === 'payments' ? secondaryTab : primaryTab;

  useEffect(() => {
    const queryTabParam = urlParams.get('tab')?.toLowerCase();
    const querySubTabParam = urlParams.get('subtab')?.toLowerCase();

    // Always ensure we have proper defaults
    if (!queryTabParam || !primaryTabs.includes(queryTabParam)) {
      // No valid tab parameter, set defaults
      navigate({
        search: QUERY.update(location.search, {
          tab: 'payments',
          subtab: 'summary',
        }),
      });
      return;
    }

    // Set the primary tab
    setPrimaryTab(queryTabParam);

    // Handle payments tab - ensure subtab is set
    if (queryTabParam === 'payments') {
      if (!querySubTabParam || !secondaryTabs.includes(querySubTabParam)) {
        // No valid subtab for payments, set default
        navigate({
          search: QUERY.update(location.search, {
            subtab: 'summary',
          }),
        });
        setSecondaryTab('summary');
      } else {
        setSecondaryTab(querySubTabParam);
      }
    }
  }, [location.search]);

  const handlePrimaryTabChange = (newTab: string) => {
    const updateParams: Record<string, string | undefined> = { tab: newTab };
    // Payments and Pre-paid (invoices) reuse the same filter param names
    // (`status`, `amount1`, …) but with incompatible values — e.g. transaction
    // statuses (`pending,successful,…`) are not valid invoice statuses, so they
    // leak across and make the invoices query return nothing. Clear them when
    // actually switching primary tabs so each view starts from a clean filter.
    if (newTab !== primaryTab) {
      updateParams.status = undefined;
      updateParams.payment_type = undefined;
      updateParams.amount1 = undefined;
      updateParams.amount2 = undefined;
      updateParams.condition = undefined;
      updateParams.search = undefined;
    }
    if (newTab === 'payments') {
      // Only set default subtab if we're not already on payments tab
      if (primaryTab !== 'payments') {
        updateParams.subtab = 'summary';
        setPrimaryTab('payments');
        setSecondaryTab('summary');
      }
      // If already on payments tab, preserve current subtab
    } else {
      // Remove subtab for invoices
      updateParams.subtab = undefined;
      setPrimaryTab(newTab);
    }
    navigate({
      search: QUERY.update(location.search, updateParams),
    });
  };

  const handleSecondaryTabChange = (newTab: string) => {
    navigate({
      search: QUERY.update(location.search, {
        subtab: newTab,
      }),
    });
  };

  const handleRefresh = () => {
    setTriggerRefresh((value) => !value);
  };

  return (
    <div className="rounded-lg">
      <PageHeading
        heading="Transactions"
        subHeading="View summary and details of your transactions here"
        className="mb-7"
        headingAppendRight={
          // isLoading is a counter, not a flag; it can go <= 0 (even negative
          // when reset() zeroes it mid-flight), so `!payment.isLoading` would
          // hide the refresh button + "last updated". Show it whenever no fetch
          // is in flight, i.e. the counter is not positive.
          payment.isLoading <= 0 ? (
            <RefreshButton onRefresh={handleRefresh} activeTab={activeTab} />
          ) : undefined
        }
      />

      <DateRangeBar
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        comparisonFrom={comparisonFrom}
        comparisonTo={comparisonTo}
        showDateFilter={true}
        className="mb-5"
      />

      {/* Primary Tabs - only show if more than one tab */}
      {primaryTabs.length > 1 && (
        <Tabs
          value={primaryTab}
          onValueChange={handlePrimaryTabChange}
          id="transaction-primary-tabs"
        >
          <TabsList className="w-full justify-start gap-0 rounded-none border-b border-gray-blue-10 bg-transparent p-0 px-8 -mx-8 h-auto">
            {primaryTabs.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                data-testid={`${tab}-tab`}
                id={`${tab}-tab`}
                className="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-blue-50 bg-transparent shadow-none data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:shadow-none"
              >
                {tab === 'invoices'
                  ? 'Pre-paid'
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Payments Tab with Sub-tabs */}
          <TabsContent value="payments" className="mt-0">
            {primaryTab === 'payments' && (
              <Tabs
                value={secondaryTab}
                onValueChange={handleSecondaryTabChange}
                id="transaction-secondary-tabs"
              >
                <TabsList className="w-full justify-start gap-0 rounded-none border-b border-gray-blue-10 bg-transparent p-0 px-8 -mx-8 h-auto mt-3 mb-6">
                  {secondaryTabs.map((tab) => (
                    <TabsTrigger
                      key={tab}
                      value={tab}
                      data-testid={`${tab}-subtab`}
                      id={`${tab}-subtab`}
                      className="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-blue-50 bg-transparent shadow-none data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:shadow-none"
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="summary" className="mt-0">
                  <DispensaryTransactionsList
                    dateFrom={dateFromISO}
                    dateTo={dateToISO}
                    prevDateFrom={comparisonFrom}
                    prevDateTo={comparisonTo}
                    triggerRefresh={triggerRefresh}
                    isActiveTab={
                      primaryTab === 'payments' && secondaryTab === 'summary'
                    }
                  />
                </TabsContent>
                <TabsContent value="details" className="mt-0">
                  <TransactionDetailsTab
                    dateFrom={dateFromISO}
                    dateTo={dateToISO}
                    triggerRefresh={triggerRefresh}
                    isActiveTab={
                      primaryTab === 'payments' && secondaryTab === 'details'
                    }
                  />
                </TabsContent>
              </Tabs>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            {primaryTab === 'invoices' && (
              <InvoicesTab
                dateFrom={dateFromISO}
                dateTo={dateToISO}
                triggerRefresh={triggerRefresh}
                isActiveTab={primaryTab === 'invoices'}
              />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default DispensaryTransactions;
